/**
 * Simplification d'un tracé GPX importé dans le planificateur.
 * - simplifyPathToWaypoints : quelques waypoints éditables (croquis recalé par ORS)
 * - simplifyPathPreservingShape : géométrie fidèle allégée (fond de plan)
 * Conservent toujours le départ et l'arrivée.
 */

const DEFAULT_MAX_WAYPOINTS = 40

/** Tolérance de départ (m) : écart max toléré entre le tracé simplifié et l'original. */
const SHAPE_TOLERANCE_M = 5
/** Plafond de sommets par tracé (affichage carte, sauvegarde locale du fond de plan). */
const MAX_SHAPE_POINTS = 8000

const EARTH_RADIUS_M = 6371000

function haversineM(a: [number, number], b: [number, number]): number {
  const toRad = Math.PI / 180
  const dLat = (b[1] - a[1]) * toRad
  const dLng = (b[0] - a[0]) * toRad
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a[1] * toRad) * Math.cos(b[1] * toRad) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
}

function dedupeConsecutive(coords: [number, number][]): [number, number][] {
  if (coords.length < 2) return coords
  const out: [number, number][] = [coords[0]]
  for (let i = 1; i < coords.length; i++) {
    const prev = out[out.length - 1]
    const cur = coords[i]
    if (Math.abs(prev[0] - cur[0]) > 1e-7 || Math.abs(prev[1] - cur[1]) > 1e-7) {
      out.push(cur)
    }
  }
  return out
}

/**
 * Waypoints répartis régulièrement le long de la distance parcourue (et non de l'index
 * des points : un GPX enregistré est bien plus dense dans les passages lents).
 */
export function simplifyPathToWaypoints(
  coords: [number, number][],
  maxPoints = DEFAULT_MAX_WAYPOINTS,
): [number, number][] {
  if (coords.length < 2) return coords
  if (coords.length <= maxPoints) return dedupeConsecutive(coords)

  const cumulative: number[] = [0]
  for (let i = 1; i < coords.length; i++) {
    cumulative.push(cumulative[i - 1] + haversineM(coords[i - 1], coords[i]))
  }
  const total = cumulative[cumulative.length - 1]
  if (total <= 0) return dedupeConsecutive([coords[0], coords[coords.length - 1]])

  const result: [number, number][] = [coords[0]]
  let j = 0
  for (let k = 1; k < maxPoints - 1; k++) {
    const target = (total * k) / (maxPoints - 1)
    while (j < coords.length - 1 && cumulative[j] < target) j++
    result.push(coords[j])
  }
  result.push(coords[coords.length - 1])
  return dedupeConsecutive(result)
}

export function pathLengthM(coords: [number, number][]): number {
  let total = 0
  for (let i = 1; i < coords.length; i++) total += haversineM(coords[i - 1], coords[i])
  return total
}

/** En deçà, la fin d'un tracé et le début du suivant sont considérés comme jointifs. */
const JOIN_TOLERANCE_M = 30

/**
 * Fusionne plusieurs tracés (dans l'ordre) en waypoints pour ORS : chaque tracé garde
 * son départ et son arrivée — les trous entre fichiers sont donc routés par ORS — et
 * reçoit une part des waypoints proportionnelle à sa longueur. Jamais plus de maxPoints :
 * au-delà (trop de segments), le résultat est rééchantillonné et certains bouts sautent.
 */
export function mergePathsToWaypoints(
  paths: [number, number][][],
  maxPoints = DEFAULT_MAX_WAYPOINTS,
): [number, number][] {
  const valid = paths.filter((p) => p.length >= 2)
  if (valid.length === 0) return []
  const lengths = valid.map(pathLengthM)
  const total = lengths.reduce((a, b) => a + b, 0)
  const budget = Math.max(maxPoints, valid.length * 2)

  const result: [number, number][] = []
  valid.forEach((path, i) => {
    const share = total > 0 ? lengths[i] / total : 1 / valid.length
    const waypoints = simplifyPathToWaypoints(path, Math.max(2, Math.round(budget * share)))
    const prev = result[result.length - 1]
    const start = prev && haversineM(prev, waypoints[0]) < JOIN_TOLERANCE_M ? 1 : 0
    result.push(...waypoints.slice(start))
  })
  const merged = dedupeConsecutive(result)
  return merged.length > maxPoints ? simplifyPathToWaypoints(merged, maxPoints) : merged
}

/** Distance (m) du point p au segment [a, b], en projection plane locale. */
function perpendicularDistanceM(
  p: [number, number],
  a: [number, number],
  b: [number, number],
): number {
  const toRad = Math.PI / 180
  const kx = Math.cos(a[1] * toRad) * EARTH_RADIUS_M * toRad
  const ky = EARTH_RADIUS_M * toRad
  const px = (p[0] - a[0]) * kx
  const py = (p[1] - a[1]) * ky
  const bx = (b[0] - a[0]) * kx
  const by = (b[1] - a[1]) * ky
  const len2 = bx * bx + by * by
  if (len2 === 0) return Math.hypot(px, py)
  const t = Math.max(0, Math.min(1, (px * bx + py * by) / len2))
  return Math.hypot(px - t * bx, py - t * by)
}

/** Douglas-Peucker itératif (pas de récursion : un GPX peut compter 100 000 points). */
function douglasPeuckerIndices(coords: [number, number][], toleranceM: number): number[] {
  const keep = new Uint8Array(coords.length)
  keep[0] = 1
  keep[coords.length - 1] = 1
  const stack: [number, number][] = [[0, coords.length - 1]]
  while (stack.length > 0) {
    const [start, end] = stack.pop()!
    let maxDist = 0
    let maxIdx = -1
    for (let i = start + 1; i < end; i++) {
      const d = perpendicularDistanceM(coords[i], coords[start], coords[end])
      if (d > maxDist) {
        maxDist = d
        maxIdx = i
      }
    }
    if (maxIdx !== -1 && maxDist > toleranceM) {
      keep[maxIdx] = 1
      stack.push([start, maxIdx], [maxIdx, end])
    }
  }
  const indices: number[] = []
  for (let i = 0; i < keep.length; i++) if (keep[i]) indices.push(i)
  return indices
}

/** Allège un tracé avec une tolérance donnée (écart maximal au tracé d'origine, en mètres). */
export function simplifyWithTolerance(coords: [number, number][], toleranceM: number): [number, number][] {
  if (coords.length <= 2 || toleranceM <= 0) return coords
  return douglasPeuckerIndices(coords, toleranceM).map((i) => coords[i])
}

/**
 * Allège un tracé en gardant sa forme (écart ≤ quelques mètres).
 * La tolérance double tant que le résultat dépasse maxPoints.
 */
export function simplifyPathPreservingShape(
  coords: [number, number][],
  maxPoints = MAX_SHAPE_POINTS,
): [number, number][] {
  if (coords.length <= 2) return coords
  let tolerance = SHAPE_TOLERANCE_M
  let indices = douglasPeuckerIndices(coords, tolerance)
  while (indices.length > maxPoints) {
    tolerance *= 2
    indices = douglasPeuckerIndices(coords, tolerance)
  }
  return indices.map((i) => coords[i])
}
